import { Component } from '@theme/component';
import { DrawerMutationQueue } from './ltl-drawer-mutation-queue.js';
const drawerMutationQueues = new Map();
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    if (this.isDrawer) return this.#observeDrawerUpdate(event);
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    if (this.isDrawer) return this.#observeDrawerUpdate(event);
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    if (this.isDrawer) this.#bindDrawerQueue();
  }

  disconnectedCallback() {
    if (this.isDrawer && this.#drawerAdapter) this.#drawerQueue().unbind(this.#drawerAdapter);
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /** Drawer adapter is rebound after hydration without losing accepted intentions. */
  #drawerAdapter = null;

  #drawerRows() {
    return Array.from(this.querySelectorAll('[ref="cartItemRows[]"]'));
  }

  #drawerQueue() {
    let queue = drawerMutationQueues.get(this.sectionId);
    if (!queue) {
      queue = new DrawerMutationQueue();
      drawerMutationQueues.set(this.sectionId, queue);
    }
    return queue;
  }

  #bindDrawerQueue() {
    const queue = this.#drawerQueue();
    this.#drawerAdapter = {
      resolve: (key) => {
        const rows = this.#drawerRows();
        const matches = rows.filter((row) => row.dataset.key === key);
        if (matches.length > 1) throw new Error('Ambiguous cart item key');
        if (!matches.length && key.includes(':') && rows.some((row) => row.dataset.key?.split(':')[0] === key.split(':')[0])) {
          throw Object.assign(new Error('Cart item identity changed'), { code: 'ITEM_IDENTITY_CHANGED' });
        }
        return matches.length ? rows.indexOf(matches[0]) + 1 : null;
      },
      perform: (operation, line, revision) => this.#performQuantityUpdate({
        line,
        quantity: operation.quantity,
        action: operation.quantity === 0 ? 'clear' : 'change',
        drawerKey: operation.key,
        drawerRevision: revision,
      }),
      refresh: async (revision) => {
        // Do not join a renderer request that started before the mutation.
        const previousKeys = new Set([...queue.observedKeys, ...this.#drawerRows().map((row) => row.dataset.key)]);
        const url = new URL(window.location.href);
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.sectionId);
        const options = { cache: 'no-store', credentials: 'same-origin' };
        const [html, cart] = await Promise.all([
          fetch(url, options).then((response) => {
            if (!response.ok) throw new Error('Cart section could not be refreshed');
            return response.text();
          }),
          fetch(`${Theme.routes.cart_url}.js`, options).then((response) => {
            if (!response.ok) throw new Error('Cart contents could not be refreshed');
            return response.json();
          }),
        ]);
        if (queue.revision !== revision || queue.externalPending) return { stale: true };
        // The two read endpoints are not an atomic snapshot. Never announce a
        // count or resume a queued mutation if their line identities disagree.
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const rows = Array.from(parsed.querySelectorAll('[ref="cartItemRows[]"]'));
        const count = Number(parsed.querySelector('[ref="cartItemCount"]')?.textContent ?? 0);
        if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 ||
          count !== cart.item_count || rows.length !== cart.items.length ||
          new Set(cart.items.map((item) => item.key)).size !== cart.items.length ||
          cart.items.some((item) => typeof item.key !== 'string' || !item.key || !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
            !rows.some((row) => row.dataset.key === item.key && Number(row.querySelector('input[name="updates[]"]')?.value) === item.quantity)) ||
          cart.items.reduce((total, item) => total + item.quantity, 0) !== cart.item_count) {
          throw new Error('Cart read results do not describe the same contents');
        }
        const render = this.#withDrawerCloseFocus(() => morphSection(this.sectionId, html, { mode: 'hydration', injectStylesheet: true }));
        this.#updateCartQuantitySelectorButtonStates();
        await render;
        if (queue.revision !== revision || queue.externalPending || !queue.adapter) return { stale: true };
        const lines = cart.items.map((item) => ({ id: item.key, quantity: item.quantity }));
        for (const key of previousKeys) {
          if (key && !cart.items.some((item) => item.key === key)) lines.push({ id: key, quantity: 0 });
        }
        // Hydration may have replaced `this`; publish from the connected owner.
        for (const key of previousKeys) queue.observedKeys.delete(key);
        queue.adapter.publish(cart, html, lines);
        return { stale: false };
      },
      publish: (cart, html, lines) => {
        this.#updateQuantitySelectors(cart);
        this.#updateCartQuantitySelectorButtonStates();
        // An already empty cart with no observed line needs no synthetic mutation.
        if (!lines.length) return;
        this.dispatchEvent(new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines,
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { sections: { [this.sectionId]: html }, items: cart.items, itemCount: cart.item_count, source: 'cart-items-component', didError: false },
          }),
        }));
      },
      notify: (snapshot) => this.#renderDrawerStatus(snapshot),
    };
    queue.bind(this.#drawerAdapter);
  }

  #focusDrawerClose(row) {
    const drawer = this.closest('dialog[open]');
    if (!drawer || !row?.contains(document.activeElement)) return;
    const close = Array.from(drawer.querySelectorAll('button')).find(
      (button) => button.getAttribute('on:click') === '#cart-drawer/close'
    );
    close?.focus({ preventScroll: true });
  }

  #withDrawerCloseFocus(render) {
    const drawer = this.closest('dialog[open]');
    const focused = document.activeElement;
    if (!drawer || !(focused instanceof HTMLElement) || !drawer.contains(focused) ||
      focused.getAttribute('on:click') !== '#cart-drawer/close') return render();

    let cancelled = false;
    const cancel = () => { cancelled = true; };
    const onFocus = (event) => {
      if (event.target !== focused && event.target !== drawer && event.target !== document.body &&
        event.target !== document.documentElement) cancel();
    };
    const restore = () => {
      if (cancelled || !drawer.isConnected || !drawer.hasAttribute('open') ||
        (document.activeElement !== document.body && document.activeElement !== drawer)) return;
      const close = Array.from(drawer.querySelectorAll('button')).find(
        (button) => button.getAttribute('on:click') === '#cart-drawer/close'
      );
      close?.focus({ preventScroll: true });
    };
    const cleanup = () => {
      document.removeEventListener('focusin', onFocus, true);
      document.removeEventListener('pointerdown', cancel, true);
      document.removeEventListener('keydown', cancel, true);
      window.removeEventListener('blur', cancel);
    };
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('pointerdown', cancel, true);
    document.addEventListener('keydown', cancel, true);
    window.addEventListener('blur', cancel);
    try {
      const result = render();
      restore();
      return Promise.resolve(result).then(restore).finally(cleanup);
    } catch (error) {
      cleanup();
      throw error;
    }
  }

  #enqueueDrawerQuantity(row, quantity, retry = false) {
    if (!row || !this.contains(row) || !row.dataset.key) return;
    const queue = this.#drawerQueue();
    if (queue.isRemovalPending(row.dataset.key, row.dataset.parentKey) && quantity > 0) return;
    if (quantity === 0) this.#focusDrawerClose(row);
    const relatedKeys = this.#drawerRows()
      .filter((child) => child.dataset.parentKey === row.dataset.key)
      .map((child) => child.dataset.key);
    queue.enqueue({ key: row.dataset.key, quantity, relatedKeys }, { retry });
  }

  retryDrawerUpdates() {
    if (this.isDrawer) {
      this.#focusDrawerClose(this);
      this.#drawerQueue().retry();
    }
  }

  #observeDrawerUpdate(event) {
    // A previous instance may dispatch while hydration replaces its element.
    // Such an event still belongs to this shared drawer queue.
    if (event.target instanceof CartItemsComponent && event.target.isDrawer && event.target.sectionId === this.sectionId) return;
    this.#drawerQueue().observe(event.promise, event.lines?.map((line) => line.id));
  }

  #renderDrawerStatus({ busy, paused, error }) {
    this.classList[busy ? 'add' : 'remove']('cart-items-disabled');
    this.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) this.refs.cartTotal?.shimmer();
    else resetShimmer(this);
    if (!paused) {
      for (const retry of this.querySelectorAll('[data-drawer-retry-error]')) {
        if (retry.dataset.drawerErrorFallback !== undefined) retry.remove();
        else {
          retry.textContent = '';
          retry.removeAttribute('data-drawer-retry-error');
          retry.closest('.cart-items__error')?.classList.add('hidden');
        }
      }
      return;
    }
    const en = document.documentElement.lang.toLowerCase().startsWith('en');
    const row = this.#drawerRows().find((item) => item.dataset.key === error?.key) ?? this.#drawerRows()[0];
    let message = row?.querySelector('.cart-item__error-text');
    if (message?.hasAttribute('data-drawer-retry-error')) return;
    if (message) message.closest('.cart-items__error')?.classList.remove('hidden');
    else {
      message = this.querySelector('[data-drawer-error-fallback]');
      if (!message) {
        message = document.createElement('p');
        message.className = 'cart-item__error-text';
        message.setAttribute('role', 'alert');
        message.setAttribute('data-drawer-error-fallback', '');
        this.prepend(message);
      }
    }
    message.setAttribute('data-drawer-retry-error', '');
    const identityChanged = error?.code === 'ITEM_IDENTITY_CHANGED';
    message.textContent = identityChanged
      ? (en ? 'An item’s reference has changed. Reload the cart, then make your choice again. ' : 'La référence d’un article a changé. Actualisez le panier, puis répétez votre choix. ')
      : (en ? 'We could not confirm the cart update. Retry to check its current contents before continuing. ' : 'La modification du panier n’a pas pu être confirmée. Réessayez pour vérifier son contenu avant de continuer. ');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button button--secondary';
    retry.textContent = identityChanged ? (en ? 'Reload cart' : 'Actualiser le panier') : (en ? 'Retry' : 'Réessayer');
    retry.addEventListener('click', () => identityChanged ? window.location.reload() : this.retryDrawerUpdates());
    message.append(retry);
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.isDrawer && this.#drawerAdapter) this.#renderDrawerStatus(this.#drawerQueue().snapshot);
  }

  updateQuantity(config) {
    if (this.isDrawer) {
      return this.#enqueueDrawerQuantity(this.#drawerRows()[config.line - 1], config.quantity);
    }
    return this.#performQuantityUpdate(config);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    if (this.isDrawer) {
      const row = event.target instanceof Element ? event.target.closest('[ref="cartItemRows[]"]') : null;
      this.#enqueueDrawerQuantity(row, event.detail.quantity);
      return;
    }
    if (
      !this.isDrawer &&
      event.target instanceof Element &&
      event.target.closest('[ref="cartItemRows[]"]')?.classList.contains('removing')
    ) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, event) {
    if (this.isDrawer) {
      const row = event?.target instanceof Element
        ? event.target.closest('[ref="cartItemRows[]"]')
        : this.#drawerRows()[line - 1];
      return this.#enqueueDrawerQuantity(row, 0, true);
    }
    if (!this.isDrawer && this.classList.contains('cart-items-disabled')) return;

    // Keep keyboard focus on a stable control while the drawer rows are replaced.
    const focusedRow = this.refs.cartItemRows[line - 1];
    const openDrawer = this.isDrawer ? this.closest('dialog[open]') : null;
    if (openDrawer && focusedRow?.contains(document.activeElement)) {
      const closeButton = Array.from(openDrawer.querySelectorAll('button')).find(
        (button) => button.getAttribute('on:click') === '#cart-drawer/close'
      );
      closeButton?.focus({ preventScroll: true });
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (this.isDrawer && isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.drawerKey] - Stable drawer item key.
   * @param {number} [config.drawerRevision] - External mutation revision at dispatch.
   */
  #performQuantityUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    if (this.isDrawer && this.#drawerRows()[line - 1]?.dataset.key !== config.drawerKey) {
      throw new Error('Cart line changed before mutation');
    }
    const { cartTotal } = this.refs;

    // Restore page-cart focus only when the removed row owned it.
    const removedRow = this.refs.cartItemRows[line - 1];
    const pageRemovalFocus =
      !this.isDrawer && quantity === 0 && removedRow?.contains(document.activeElement)
        ? { sectionId: this.sectionId, pathname: window.location.pathname, cancelled: false }
        : null;
    const cancelPageRemovalFocus = () => {
      if (pageRemovalFocus) pageRemovalFocus.cancelled = true;
    };
    /** @param {FocusEvent} event */
    const onPageRemovalFocusIn = (event) => {
      if (event.target !== document.body && event.target !== document.documentElement) cancelPageRemovalFocus();
    };
    if (pageRemovalFocus) {
      document.addEventListener('focusin', onPageRemovalFocusIn, true);
      document.addEventListener('pointerdown', cancelPageRemovalFocus, true);
      document.addEventListener('keydown', cancelPageRemovalFocus, true);
      window.addEventListener('blur', cancelPageRemovalFocus);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(this.isDrawer ? { id: config.drawerKey } : { line: line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    let drawerHttpStatus = 200;
    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        if (this.isDrawer) drawerHttpStatus = response.status ?? 200;
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        if (this.isDrawer && drawerHttpStatus >= 400 && !(drawerHttpStatus === 422 && parsedResponseText.errors)) {
          throw new Error(parsedResponseText.description || parsedResponseText.message || 'Cart response could not be confirmed');
        }

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return this.isDrawer ? { rejected: true, error: new Error(parsedResponseText.errors) } : undefined;
        }

        if (this.isDrawer && !Array.isArray(parsedResponseText.items)) throw new Error('Invalid cart response');
        if (this.isDrawer && (config.drawerRevision !== this.#drawerQueue().revision || typeof parsedResponseText.sections?.[this.sectionId] !== 'string')) {
          // Existing consumers ignore AbortError, keeping their latest confirmed
          // count until reconciliation publishes a fresh authoritative snapshot.
          deferredUpdatePromise.reject(Object.assign(new Error('Cart response superseded by reconciliation'), { name: 'AbortError' }));
          return { ok: true, stale: true };
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        const sectionUpdate = this.isDrawer
          ? this.#withDrawerCloseFocus(() => morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: 'hydration' }))
          : morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: 'full' });
        const finishSectionUpdate = () => {
          this.#updateCartQuantitySelectorButtonStates();

          if (
            pageRemovalFocus &&
            !pageRemovalFocus.cancelled &&
            window.location.pathname === pageRemovalFocus.pathname &&
            document.activeElement === document.body
          ) {
            const currentCart = Array.from(document.querySelectorAll('cart-items-component')).find(
              (item) =>
                item instanceof HTMLElement &&
                item.dataset.drawer === undefined &&
                item.dataset.sectionId === pageRemovalFocus.sectionId
            );
            const currentRows = currentCart?.querySelectorAll('[ref="cartItemRows[]"]');
            const nextRow = currentRows?.[Math.min(line - 1, currentRows.length - 1)];
            const focusTarget =
              nextRow?.querySelector('a.cart-items__title[href]') ??
              nextRow?.querySelector('a[href]') ??
              currentCart?.querySelector('.cart-items__empty-button');
            if (focusTarget instanceof HTMLElement && focusTarget.isConnected) {
              focusTarget.focus({ preventScroll: true });
            }
          }
        };
        if (pageRemovalFocus) {
          return Promise.resolve(sectionUpdate).then(finishSectionUpdate);
        }
        finishSectionUpdate();
        if (this.isDrawer) return Promise.resolve(sectionUpdate).then(() => ({ ok: true }));
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        return this.isDrawer ? { error } : undefined;
      })
      .finally(() => {
        if (pageRemovalFocus) {
          document.removeEventListener('focusin', onPageRemovalFocusIn, true);
          document.removeEventListener('pointerdown', cancelPageRemovalFocus, true);
          document.removeEventListener('keydown', cancelPageRemovalFocus, true);
          window.removeEventListener('blur', cancelPageRemovalFocus);
        }
        if (!this.isDrawer) this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;
    if (this.isDrawer) return this.#observeDrawerUpdate(event);

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
